import { useEffect } from "react";
import { useAuth } from "@/lib/auth";

function hexToHslTriplet(hex: string): string | null {
  const m = hex.replace("#", "").trim();
  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(m)) return null;
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  const H = Math.round(h * 360);
  const S = Math.round(s * 100);
  const L = Math.round(l * 100);
  return `${H} ${S}% ${L}%`;
}

/**
 * Reads the signed-in user's organization brand color and pushes it
 * into the global CSS variables that the entire shadcn theme reads
 * from (--primary, --ring, --accent, --sidebar-primary). The result
 * is that every accent — buttons, focus rings, sidebar highlight,
 * brand glows — automatically takes on the tenant's color in both
 * light and dark mode without touching any individual page.
 */
export function TenantThemeVars() {
  const { organization } = useAuth();
  const brand = organization?.brandColor || null;

  useEffect(() => {
    const root = document.documentElement;
    const triplet = brand ? hexToHslTriplet(brand) : null;
    const VARS = ["--primary", "--ring", "--accent", "--sidebar-primary", "--sidebar-ring"];
    if (!triplet) {
      VARS.forEach((v) => root.style.removeProperty(v));
      root.style.removeProperty("--brand-color");
      return;
    }
    VARS.forEach((v) => root.style.setProperty(v, triplet));
    root.style.setProperty("--brand-color", brand!);
  }, [brand]);

  return null;
}
